//
/* GS://PROTOCOLO
 * Arquivo: src/pages/lots/LotBathPage.jsx
 * Dossiê de Projeto e Colaboração: Ecossistema GoldenSisters
 * Versão: 3.1 – FIX: Interface Completa + Resgate Legado
 * Impacto da Versão: [src/pages/lots/LotBathPage.jsx]
 * Data: 26/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [RESGATE]: Implementado fallback que busca itens de lotes antigos (sem statusPiece) caso a busca padrão falhe.
 * - [FINALIZAÇÃO]: Ao processar estes itens recuperados, o lote será automaticamente FINALIZADO no banco.
 * - [PERFORMANCE]: Substituída a busca pesada de todas as NFs do sistema por um input ágil de "Valor Total do Serviço".
 */
//

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import bathService from '../../services/bathService';
import lotsService from '../../services/lotsService'; // Import essencial para o resgate legado
import { getSuppliers } from '../../services/firestoreService';
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  AlertTriangle, 
  CheckCircle2, 
  Scale,
  Calculator,
  AlertCircle,
  DollarSign
} from 'lucide-react';

const LotBathPage = () => {
  const { id: lotId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Dados do Lote e Itens
  const [lot, setLot] = useState(null);
  const [items, setItems] = useState([]); 

  // Fornecedores
  const [serviceProviders, setServiceProviders] = useState([]);
  
  // Inputs do Formulário
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [serviceTotalValue, setServiceTotalValue] = useState(''); // Input do Valor Total (R$)
  const [bathPricePerGram, setBathPricePerGram] = useState(''); // Calculado ou Manual (R$/g)
  const [extraCost, setExtraCost] = useState(''); // Custo fixo (opcional)

  // Estado de Seleção { [itemId]: boolean }
  const [checkMap, setCheckMap] = useState({});

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        
        // 1. Tenta carregar pelo serviço padrão de banho (busca itens com status 'BRUTA')
        let { lot: lotData, items: itemsData } = await bathService.getLotBathData(lotId);
        
        // [MECANISMO DE RESGATE]: Se não achou itens no padrão novo e o lote ainda está ABERTO
        if (itemsData.length === 0 && lotData && lotData.statusLot !== 'FINALIZADO') {
            console.warn('Busca padrão vazia. Ativando protocolo de resgate legado...');
            try {
                // Busca TODOS os itens do lote sem filtro
                const allItems = await lotsService.getLotItems(lotId);
                
                // Filtra itens que parecem ser brutos (sem status definido ou explicitamente bruta)
                itemsData = allItems.filter(it => 
                    !it.statusPiece || // Legado (sem status = assume bruta para processar)
                    it.statusPiece === 'BRUTA' || 
                    it.itemType === 'BRUTA'
                ).map(it => ({
                    // Normaliza os dados antigos para o formato que a tela espera
                    id: it.id,
                    ref: it.ref,
                    sku: it.supplierProductCode || it.sku,
                    description: it.descriptionNF || it.description,
                    statusPiece: it.statusPiece || 'BRUTA', // Força status visual
                    quantity: Number(it.quantity) || 0,
                    weightGrams: Number(it.weightGrams) || 0,
                    costBruto: Number(it.costBruto || it.costTotalInicial || 0),
                    stockRef: it.stockRef || null
                }));
            } catch (legacyErr) {
                console.error("Erro ao recuperar legado:", legacyErr);
            }
        }

        setLot(lotData);
        setItems(itemsData);

        // Marca todos como selecionados por padrão para facilitar
        const initialMap = {};
        itemsData.forEach(it => { initialMap[it.id] = true; });
        setCheckMap(initialMap);

        // 2. Carrega fornecedores para o select
        const allSuppliers = await getSuppliers();
        setServiceProviders(allSuppliers);

      } catch (err) {
        console.error(err);
        setError(err.message || 'Erro ao carregar dados do lote.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [lotId]);

  // --- CÁLCULOS MATEMÁTICOS ---

  const selectedItems = useMemo(() => {
    return items.filter(it => checkMap[it.id]);
  }, [items, checkMap]);

  const totalWeightSelected = useMemo(() => {
    return selectedItems.reduce((acc, it) => acc + (Number(it.weightGrams) || 0), 0);
  }, [selectedItems]);

  // [AUTOMAÇÃO]: Recalcula preço por grama quando o Valor Total muda
  useEffect(() => {
      if (serviceTotalValue && totalWeightSelected > 0) {
          const calculated = Number(serviceTotalValue) / totalWeightSelected;
          setBathPricePerGram(calculated.toFixed(4));
      }
  }, [serviceTotalValue, totalWeightSelected]);

  const estimatedBathCost = useMemo(() => {
    // Se digitou total, usa o total. Senão calcula pela grama.
    if (serviceTotalValue) return Number(serviceTotalValue) + (Number(extraCost) || 0);
    
    const price = Number(bathPricePerGram) || 0;
    const extra = Number(extraCost) || 0;
    return (totalWeightSelected * price) + extra;
  }, [totalWeightSelected, bathPricePerGram, extraCost, serviceTotalValue]);


  // --- AÇÕES ---

  const handleToggleItem = (itemId) => {
    setCheckMap(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleToggleAll = () => {
    const allSelected = selectedItems.length === items.length;
    const newMap = {};
    items.forEach(it => { newMap[it.id] = !allSelected; });
    setCheckMap(newMap);
  };

  const handleSave = async () => {
    if (!bathPricePerGram || Number(bathPricePerGram) <= 0) {
      alert('Preço por grama inválido. Informe o Valor Total do serviço ou o preço da grama.');
      return;
    }
    if (selectedItems.length === 0) {
      alert('Selecione ao menos uma peça.');
      return;
    }
    
    if (!window.confirm(`Confirma o envio de ${selectedItems.length} peças para o banho? Isso finalizará o lote.`)) {
      return;
    }

    try {
      setIsSaving(true);
      setError('');
      
      await bathService.applyBathToLot({
        lotId,
        lotCode: lot.lotCode || lot.lotNumber,
        nfNumber: lot.nfNumber,
        bathPricePerGram: Number(bathPricePerGram),
        extraCost: Number(extraCost) || 0,
        serviceProvider: selectedProviderId ? serviceProviders.find(s => s.id === selectedProviderId)?.name : 'Interno'
      });

      setSuccessMsg('Banho registrado com sucesso! Estoque atualizado e Lote Finalizado.');
      setTimeout(() => {
        navigate('/lotes');
      }, 2000);

    } catch (err) {
      console.error(err);
      setError(err.message || 'Erro ao salvar banho.');
    } finally {
      setIsSaving(false);
    }
  };

  // --- RENDERIZAÇÃO ---

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-brand-black text-white">
        <Loader2 className="animate-spin h-8 w-8 text-brand-gold" />
      </div>
    );
  }

  // Trava para lote realmente vazio (sem legado, sem nada)
  if (!loading && items.length === 0) {
    return (
      <div className="min-h-screen bg-brand-black text-white p-6 flex flex-col items-center justify-center">
        <div className="max-w-md w-full bg-brand-dark p-8 rounded-lg border border-gray-700 text-center shadow-xl">
          <AlertCircle className="h-16 w-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Nenhuma Peça Pendente</h2>
          <p className="text-gray-400 mb-6">
            Não há peças brutas disponíveis neste lote (nem legadas).
            Provavelmente o lote já foi finalizado ou é apenas de serviço.
          </p>
          <button onClick={() => navigate('/lotes')} className="w-full py-2 bg-brand-gold text-brand-black font-bold rounded">
            Voltar para Lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black text-white p-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/lotes')} className="p-2 hover:bg-gray-800 rounded-full transition-colors">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              Aplicação de Banho <span className="text-brand-gold font-mono">#{lot?.lotCode || lot?.lotNumber}</span>
            </h1>
            <p className="text-gray-400 text-sm">Fornecedor: {lot?.supplierName} • NF: {lot?.nfNumber}</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-600 rounded flex items-center gap-3 text-red-200">
            <AlertTriangle className="h-5 w-5" /> <p>{error}</p>
          </div>
        )}

        {successMsg && (
          <div className="mb-6 p-4 bg-green-900/30 border border-green-600 rounded flex items-center gap-3 text-green-200">
            <CheckCircle2 className="h-5 w-5" /> <p>{successMsg}</p>
          </div>
        )}

        {/* Resumo do Lote */}
        {lot && (
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-6">
              <h2 className="text-xl font-semibold mb-3 text-brand-black">
                Resumo do Lote
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Código do Lote</p>
                  <p className="font-semibold text-brand-black">{lot.lotCode}</p>
                </div>
                <div>
                  <p className="text-gray-500">NF</p>
                  <p className="font-semibold text-brand-black">{lot.nfNumber}</p>
                </div>
                <div>
                  <p className="text-gray-500">Data da NF</p>
                  <p className="font-semibold text-brand-black">{lot.nfDateDisplay || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Fornecedor</p>
                  <p className="font-semibold text-brand-black">{lot.supplierName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <p className="font-semibold text-brand-gold">{lot.statusLot}</p>
                </div>
                <div>
                  <p className="text-gray-500">Itens BRUTOS (Elegíveis)</p>
                  <p className="font-semibold text-brand-black">{items.length}</p>
                </div>
                <div>
                  <p className="text-gray-500">Peso Total (Selecionado)</p>
                  <p className="font-semibold text-brand-black">{totalWeightSelected.toFixed(2)} g</p>
                </div>
              </div>
            </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* PAINEL DE CUSTOS (ESQUERDA) */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-brand-dark p-5 rounded-lg border border-gray-700 shadow-lg">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 border-b border-gray-700 pb-2">
                <Calculator className="h-5 w-5 text-brand-gold" />
                Cálculo do Serviço
              </h2>
              
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Quem fará o serviço?</label>
                  <select
                    value={selectedProviderId}
                    onChange={(e) => setSelectedProviderId(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:border-brand-gold focus:ring-1 focus:ring-brand-gold outline-none"
                  >
                    <option value="">Selecione...</option>
                    {serviceProviders.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>

                {/* Input de Valor Total - Simplificação inteligente */}
                <div className="p-4 bg-gray-800/80 rounded border border-gray-600 relative">
                    <label className="block text-xs font-bold text-green-400 uppercase mb-1">Valor Total da Nota (R$)</label>
                    <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-lg font-light">R$</span>
                        <input
                          type="number" step="0.01"
                          value={serviceTotalValue}
                          onChange={(e) => setServiceTotalValue(e.target.value)}
                          placeholder="0.00"
                          className="w-full bg-transparent border-none focus:ring-0 text-white font-mono text-2xl p-0 font-bold placeholder-gray-600"
                        />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2">
                      O sistema dividirá este valor pelo peso total ({totalWeightSelected.toFixed(2)}g) para achar o custo/grama.
                    </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Custo/Grama</label>
                      <input
                        type="number" step="0.0001"
                        value={bathPricePerGram}
                        onChange={(e) => {
                            setBathPricePerGram(e.target.value);
                            setServiceTotalValue(''); // Limpa total se editar manual
                        }}
                        className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:border-brand-gold font-mono text-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Extra (Frete/Outros)</label>
                      <input
                        type="number" step="0.01"
                        value={extraCost}
                        onChange={(e) => setExtraCost(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:border-brand-gold text-gray-300"
                      />
                    </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-700">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Peso Selecionado:</span>
                  <span className="font-mono text-white">{totalWeightSelected.toFixed(2)} g</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-brand-gold">
                  <span>Total Final:</span>
                  <span>{estimatedBathCost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                </div>
              </div>

              <button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full mt-6 bg-brand-gold text-brand-black font-bold py-3 rounded hover:bg-yellow-500 transition-colors disabled:opacity-50 flex justify-center items-center gap-2 shadow-lg hover:shadow-xl transform active:scale-95 duration-150"
              >
                {isSaving ? <Loader2 className="animate-spin h-5 w-5" /> : <Save className="h-5 w-5" />}
                CONFIRMAR BANHO
              </button>
            </div>
          </div>

          {/* LISTA DE PEÇAS (DIREITA) */}
          <div className="lg:col-span-2">
            <div className="bg-brand-dark rounded-lg border border-gray-700 overflow-hidden shadow-lg flex flex-col h-full">
              <div className="p-4 bg-gray-800 border-b border-gray-700 flex justify-between items-center">
                <h2 className="font-semibold flex items-center gap-2 text-white">
                  <Scale className="h-5 w-5 text-brand-gold" />
                  Peças para Banho ({selectedItems.length}/{items.length})
                </h2>
                <button onClick={handleToggleAll} className="text-xs text-brand-gold hover:text-white transition-colors uppercase font-bold tracking-wide">
                  {selectedItems.length === items.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto max-h-[600px]">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-900/50 text-gray-400 sticky top-0 backdrop-blur-sm">
                    <tr>
                      <th className="px-4 py-3 w-12 text-center">
                        <input type="checkbox" checked={selectedItems.length === items.length && items.length > 0} onChange={handleToggleAll} className="rounded border-gray-600 bg-gray-700 text-brand-gold focus:ring-brand-gold cursor-pointer" />
                      </th>
                      <th className="px-4 py-3">Descrição do Item</th>
                      <th className="px-4 py-3 text-right">Peso (g)</th>
                      <th className="px-4 py-3 text-right">Custo Origem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {items.map(it => (
                      <tr key={it.id} className={`hover:bg-gray-800 transition-colors group ${checkMap[it.id] ? 'bg-brand-gold/5' : ''}`}>
                        <td className="px-4 py-3 text-center">
                          <input type="checkbox" checked={!!checkMap[it.id]} onChange={() => handleToggleItem(it.id)} className="rounded border-gray-600 bg-gray-700 text-brand-gold focus:ring-brand-gold cursor-pointer" />
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-200 group-hover:text-white transition-colors">{it.description}</p>
                          <div className="flex gap-2 mt-1">
                            <span className="text-[10px] bg-gray-800 px-1.5 py-0.5 rounded text-gray-400 border border-gray-700">{it.sku || 'S/ SKU'}</span>
                            <span className="text-[10px] bg-gray-800 px-1.5 py-0.5 rounded text-gray-400 border border-gray-700">{it.statusPiece}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-gray-300">{it.weightGrams?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-400">R$ {it.costBruto?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LotBathPage;